// onboarding-wizard.js — Phase 3.6: Onboarding Mode Wizard
// 4-step modal wizard for starting a new client season

import { apiFetch } from './utils.js';
import { state } from './state.js';

let activeSession = null;
let wizardClientId = null;
let wizardShowId = null;

export function initOnboardingWizard() {
  if (!document.getElementById('onboarding-modal')) {
    const modal = document.createElement('div');
    modal.id = 'onboarding-modal';
    modal.className = 'modal-overlay hidden';
    document.body.appendChild(modal);
  }
  window.launchOnboardingWizard = launchOnboardingWizard;
}

async function launchOnboardingWizard(clientId, showId) {
  wizardClientId = clientId;
  wizardShowId = showId;

  try {
    const sessions = await apiFetch(`/api/onboarding/client/${clientId}`);
    const active = sessions.find(s => !s.all_complete);
    if (active) {
      activeSession = active;
      renderWizardStep(active.current_step);
    } else {
      activeSession = null;
      renderWizardStep(1);
    }
  } catch (err) {
    activeSession = null;
    renderWizardStep(1);
  }

  document.getElementById('onboarding-modal').classList.remove('hidden');
}

function closeWizard() {
  document.getElementById('onboarding-modal').classList.add('hidden');
  activeSession = null;
}

function renderWizardStep(step) {
  const modal = document.getElementById('onboarding-modal');
  if (!modal) return;

  // Look up client from state; fall back to fetching if not cached
  const client = (state.clients || []).find(c => c.id == wizardClientId);

  modal.innerHTML = `
    <div class="wizard-dialog">
      <div class="wizard-header">
        <span class="wizard-title">Onboarding — ${client?.name || 'Client'}</span>
        <button class="wizard-close" onclick="document.getElementById('onboarding-modal').classList.add('hidden')">×</button>
      </div>
      <div class="wizard-progress">
        ${[1, 2, 3, 4].map(s => `
          <div class="wizard-step-dot ${s < step ? 'done' : s === step ? 'active' : ''}">
            <span>${s < step ? '✓' : s}</span>
            <span class="dot-label">${stepLabel(s)}</span>
          </div>
          ${s < 4 ? '<div class="wizard-step-line"></div>' : ''}
        `).join('')}
      </div>
      <div class="wizard-content" id="wizard-content">
        ${renderStepContent(step, client)}
      </div>
    </div>
  `;
}

function stepLabel(step) {
  return ['', 'Client', 'Contract', 'Signing', 'Survey'][step];
}

function renderStepContent(step, client) {
  switch (step) {
    case 1: return renderStep1(client);
    case 2: return renderStep2(client);
    case 3: return renderStep3();
    case 4: return renderStep4();
    default: return '<div>Unknown step</div>';
  }
}

function renderStep1(client) {
  return `
    <div class="wizard-step step-1">
      <h3>Step 1 — Confirm Client Record</h3>
      <p class="wizard-description">
        Verify the client and show details before we begin. These will be used throughout the onboarding.
      </p>

      <div class="wizard-field-group">
        <div class="wizard-field">
          <label>Director Name</label>
          <input type="text" id="ob-name" value="${escVal(client?.name)}" class="wizard-input">
        </div>
        <div class="wizard-field">
          <label>Email</label>
          <input type="email" id="ob-email" value="${escVal(client?.email)}" class="wizard-input">
        </div>
        <div class="wizard-field">
          <label>Phone</label>
          <input type="tel" id="ob-phone" value="${escVal(client?.phone)}" class="wizard-input">
        </div>
        <div class="wizard-field">
          <label>Program / Ensemble</label>
          <input type="text" id="ob-program" value="${escVal(client?.ensemble_type || client?.program)}" class="wizard-input">
        </div>
      </div>

      <div class="wizard-actions">
        <button class="btn-wizard-primary" onclick="window.confirmStep1()">
          Start Onboarding →
        </button>
      </div>
    </div>
  `;
}

function renderStep2(client) {
  return `
    <div class="wizard-step step-2">
      <h3>Step 2 — Send Contract</h3>
      <p class="wizard-description">
        David will send the contract from <strong>david@whitemagemusic.com</strong>.
        The contract will be generated from the show details confirmed in Step 1.
      </p>

      <div class="wizard-preview-box">
        <div class="preview-row">
          <span class="preview-label">From:</span>
          <span class="preview-value">David Thorne &lt;david@whitemagemusic.com&gt;</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">To:</span>
          <span class="preview-value">${escVal(client?.name)} &lt;${escVal(client?.email)}&gt;</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Subject:</span>
          <span class="preview-value">Your contract — White Mage Music</span>
        </div>
      </div>

      <div class="wizard-actions">
        <button class="btn-wizard-secondary" onclick="window.__wizardBack(2)">← Back</button>
        <button class="btn-wizard-primary" onclick="window.confirmStep2()">
          Send Contract from David →
        </button>
      </div>
    </div>
  `;
}

function renderStep3() {
  return `
    <div class="wizard-step step-3">
      <h3>Step 3 — After Signing</h3>
      <p class="wizard-description">
        When you mark the contract as signed, the following will happen automatically:
      </p>

      <div class="wizard-auto-actions">
        <div class="auto-action">
          <span class="auto-icon ryan">R</span>
          <div class="auto-action-text">
            <strong>Ryan's voice:</strong> Thank-you email sent from WMM account
          </div>
        </div>
        <div class="auto-action">
          <span class="auto-icon ryan">R</span>
          <div class="auto-action-text">
            <strong>Ryan's voice:</strong> Survey email sent from WMM account
          </div>
        </div>
        <div class="auto-action">
          <span class="auto-icon system">⚙</span>
          <div class="auto-action-text">
            Drive folder hierarchy provisioned (Root → Client → Year → Shares)
          </div>
        </div>
        <div class="auto-action">
          <span class="auto-icon system">⚙</span>
          <div class="auto-action-text">
            Collaborators granted Editor access to Drive folders
          </div>
        </div>
      </div>

      <p class="wizard-note">
        Mark the contract signed in the Finance tab, then return here to advance.
      </p>

      <div class="wizard-actions">
        <button class="btn-wizard-primary" onclick="window.confirmStep3()">
          Contract Is Signed — Continue →
        </button>
      </div>
    </div>
  `;
}

function renderStep4() {
  return `
    <div class="wizard-step step-4">
      <h3>Step 4 — Survey Return</h3>
      <p class="wizard-description">
        When the director returns the survey, the following will happen:
      </p>

      <div class="wizard-auto-actions">
        <div class="auto-action">
          <span class="auto-icon ryan">R</span>
          <div class="auto-action-text">
            <strong>Ryan's voice:</strong> Survey thank-you email sent
          </div>
        </div>
        <div class="auto-action">
          <span class="auto-icon david">D</span>
          <div class="auto-action-text">
            <strong>David's voice:</strong> Portal access email sent to director
          </div>
        </div>
      </div>

      <div class="wizard-actions">
        <button class="btn-wizard-primary" onclick="window.confirmStep4()">
          Survey Received — Complete Onboarding ✓
        </button>
      </div>
    </div>
  `;
}

function escVal(str) {
  return String(str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// ── Step confirmation handlers ─────────────────────────────────────────────

window.__wizardBack = function(fromStep) {
  renderWizardStep(fromStep - 1);
};

window.confirmStep1 = async function() {
  const updates = {
    name:  document.getElementById('ob-name')?.value,
    email: document.getElementById('ob-email')?.value,
    phone: document.getElementById('ob-phone')?.value,
  };

  try {
    // Update client record (use v1 clients PATCH if available)
    await apiFetch(`/api/v1/clients/${wizardClientId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    }).catch(() =>
      // fallback: some V3 routes may not have PATCH; silently continue
      null
    );

    const session = await apiFetch('/api/onboarding/start', {
      method: 'POST',
      body: JSON.stringify({ client_id: wizardClientId, show_id: wizardShowId })
    });
    activeSession = session;
    renderWizardStep(2);
  } catch (err) {
    console.error('Step 1 failed:', err);
    alert('Failed to start onboarding: ' + err.message);
  }
};

window.confirmStep2 = async function() {
  if (!activeSession) { alert('No active onboarding session'); return; }
  try {
    const updated = await apiFetch(`/api/onboarding/${activeSession.id}/step`, {
      method: 'POST',
      body: JSON.stringify({ step: 2 })
    });
    activeSession = updated;
    // Fire automation trigger to send contract from David
    apiFetch('/api/automations/trigger', {
      method: 'POST',
      body: JSON.stringify({ event: 'onboarding_contract_send', client_id: wizardClientId, show_id: wizardShowId })
    }).catch(err => console.warn('Contract send trigger failed:', err));
    renderWizardStep(3);
  } catch (err) {
    console.error('Step 2 failed:', err);
    alert('Failed to send contract: ' + err.message);
  }
};

window.confirmStep3 = async function() {
  if (!activeSession) { alert('No active onboarding session'); return; }
  try {
    const updated = await apiFetch(`/api/onboarding/${activeSession.id}/step`, {
      method: 'POST',
      body: JSON.stringify({ step: 3 })
    });
    activeSession = updated;
    renderWizardStep(4);
  } catch (err) {
    console.error('Step 3 failed:', err);
    alert('Failed to advance to step 3: ' + err.message);
  }
};

window.confirmStep4 = async function() {
  if (!activeSession) { alert('No active onboarding session'); return; }
  try {
    await apiFetch(`/api/onboarding/${activeSession.id}/step`, {
      method: 'POST',
      body: JSON.stringify({ step: 4 })
    });
    closeWizard();
    if (window.renderClientCard) {
      await window.renderClientCard(wizardClientId);
    }
  } catch (err) {
    console.error('Step 4 failed:', err);
    alert('Failed to complete onboarding: ' + err.message);
  }
};
